import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { CallRecording } from './CallRecording';
import { urgencyClass } from './CallsList';

type CallDetailRow = {
  id: string;
  accountId?: string | null;
  conversationId?: string | null;
  callerName?: string | null;
  callerNumber?: string | null;
  urgencyLevel?: string | null;
  durationSec?: number | null;
  startedAt?: string | null;
  createdAt?: string | null;
  summary?: { summaryText?: string | null } | null;
  transcript?: { content?: string | null } | null;
  // Structured provider turns ({ role, message }) — the source for the chat
  // bubbles. Falls back to the flat transcript text when absent.
  messages?: unknown;
  // Auto-linked contact for the caller's number — its saved name wins over
  // callerName (what the caller told the agent) for the header title, and its
  // relationship + notes give the "who is this / worth mentioning" context.
  contact?: {
    name?: string | null;
    relationshipTag?: string | null;
    notes?: string | null;
  } | null;
};

/** Full detail for one call — recording, summary, transcript. Fetches by id so it
 *  works both as a standalone page (customer, mobile) and a side panel (admin). */
export function CallDetail({ callId }: { callId: string }) {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const q = crud.call.useFindUnique({
    where: { id: callId },
    select: {
      id: true,
      accountId: true,
      conversationId: true,
      callerName: true,
      callerNumber: true,
      urgencyLevel: true,
      durationSec: true,
      startedAt: true,
      createdAt: true,
      summary: { select: { summaryText: true } },
      transcript: { select: { content: true } },
      messages: true,
      contact: { select: { name: true, relationshipTag: true, notes: true } },
    },
  });
  const c = q.data as CallDetailRow | null | undefined;

  // Fallback contact lookup by number, for calls linked after the fact (same reason
  // as CallsList). Scoped to the caller's own number, so it is a single-row read.
  const contactByNumberQ = crud.contact.useFindFirst(
    {
      where: { accountId: c?.accountId ?? '', e164: c?.callerNumber ?? '' },
      select: { name: true, relationshipTag: true, notes: true },
    },
    { enabled: !!c?.accountId && !!c?.callerNumber },
  );

  if (q.isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (!c) return <p className="text-sm text-muted-foreground">{t('calls.empty')}</p>;

  const fmt = (d?: string | null) =>
    d
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(d),
        )
      : '—';

  // The linked contact: the stored relation when present, else the by-number
  // lookup (calls linked after the fact). Its name wins over callerName; its
  // relationship + notes are the "who is this / worth mentioning" context.
  type ContactInfo = { name?: string | null; relationshipTag?: string | null; notes?: string | null };
  const linkedContact =
    c.contact ?? ((contactByNumberQ.data as ContactInfo | null | undefined) ?? null);
  const number = c.callerNumber || null;
  const name = linkedContact?.name?.trim() || c.callerName || null;
  const relationshipTag = linkedContact?.relationshipTag?.trim() || null;
  const notes = linkedContact?.notes?.trim() || null;
  // Structured turns for the chat bubbles. The provider array carries `role`
  // ('agent' | 'user') and `message`; anything without text is dropped.
  const turns = Array.isArray(c.messages)
    ? (c.messages as Record<string, unknown>[])
        .map((m) => {
          const r = m ?? {};
          const text = typeof r.message === 'string' ? r.message : typeof r.text === 'string' ? r.text : '';
          const role = typeof r.role === 'string' ? r.role : '';
          return { role, text: text.trim() };
        })
        .filter((m) => m.text)
    : [];

  // Number shown on the meta line only when a name occupies the title, so an
  // unknown caller's number isn't printed twice.
  const meta = [
    name && number ? number : null,
    fmt(c.startedAt || c.createdAt),
    c.durationSec != null ? `${c.durationSec}s` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-4">
      {/* Caller card — always shown. Name + relationship + urgency on top, any
          notes below, and the number/date/duration as a small footer at the
          bottom. */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <div className="truncate text-base font-semibold">
              {name || number || t('calls.caller')}
            </div>
            {relationshipTag ? (
              <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                {relationshipTag}
              </span>
            ) : null}
          </div>
          {c.urgencyLevel ? (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${urgencyClass(c.urgencyLevel)}`}
            >
              {t(`enums.${c.urgencyLevel}`, c.urgencyLevel)}
            </span>
          ) : null}
        </div>
        {notes ? (
          <p className="mt-2 whitespace-pre-wrap wrap-break-word text-sm">{notes}</p>
        ) : null}
        {meta ? <div className="mt-2 text-xs text-muted-foreground">{meta}</div> : null}
      </div>

      <CallRecording conversationId={c.conversationId ?? null} />

      <div>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('calls.summary')}
        </h3>
        <p className="mt-1 whitespace-pre-wrap wrap-break-word text-sm font-semibold">
          {c.summary?.summaryText || (
            <span className="font-normal text-muted-foreground">{t('calls.noSummary')}</span>
          )}
        </p>
      </div>

      <div>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('calls.transcript')}
        </h3>
        {turns.length > 0 ? (
          // Chat bubbles: the AI agent on the right (it's "our" side), the caller on
          // the left — the familiar messaging layout.
          <div className="mt-2 flex flex-col gap-2">
            {turns.map((m, i) => {
              const isAgent = m.role === 'agent' || m.role === 'assistant';
              return (
                <div
                  key={i}
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap wrap-break-word ${
                    isAgent
                      ? 'self-end rounded-br-sm bg-primary text-primary-foreground'
                      : 'self-start rounded-bl-sm bg-muted text-foreground'
                  }`}
                >
                  {m.text}
                </div>
              );
            })}
          </div>
        ) : c.transcript?.content ? (
          <p className="mt-1 whitespace-pre-wrap wrap-break-word text-sm">{c.transcript.content}</p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">{t('calls.noTranscript')}</p>
        )}
      </div>
    </div>
  );
}
